import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { encodeEuConsent } from './consent-encode';
import { ConsentRegionService } from './consent-region.service';
import {
  AdRequestConsentParams,
  ConsentSnapshot,
  GPP_COOKIE,
  STORAGE_KEY,
  StoredConsent,
  TCF_COOKIE,
  TCF_EU_GPP_SID,
  TCF_PURPOSE_GROUPS,
  US_PRIVACY_COOKIE,
  US_PRIVACY_NOT_APPLICABLE,
  appendConsentQuery,
  emptyConsentSnapshot,
  purposeIdsFromChoices,
} from './consent.model';

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 390;

/**
 * Client-side CMP for SRS-50. Choices live in localStorage and IAB cookies
 * (euconsent-v2, gpp, usprivacy). There is no consent API. Outbound ad-request
 * URLs pick the signals up via appendToUrl / ConsentInterceptor.
 * COPPA is not a banner choice — it is a publisher setting this dashboard does not have.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private snapshot = emptyConsentSnapshot();
  private stored = this.readStored();
  private reopened = false;

  readonly snapshot$ = new BehaviorSubject<ConsentSnapshot>(this.snapshot);
  readonly bannerOpen$ = new BehaviorSubject<boolean>(false);
  readonly saving$ = new BehaviorSubject<boolean>(false);
  readonly saveError$ = new Subject<string>();

  constructor(private readonly regionService: ConsentRegionService) {
    if (this.stored) {
      this.persist(this.stored);
    }
    this.regionService.region$.subscribe((region) => {
      if (region) {
        this.applyRegion(region.inScope, region.country);
      }
    });
  }

  refresh(): void {
    this.stored = this.readStored();
    this.regionService.refresh();
  }

  reopen(): void {
    this.reopened = true;
    if (this.snapshot.regionResolved && this.snapshot.inScope) {
      this.bannerOpen$.next(true);
    }
  }

  save(choices: Record<string, boolean>): void {
    this.saving$.next(true);
    try {
      const encoded = encodeEuConsent(
        purposeIdsFromChoices(choices),
        this.snapshot.region || 'DE',
      );
      const stored: StoredConsent = {
        version: 1,
        region: this.snapshot.region,
        choices: { ...choices },
        tcString: encoded.tcString,
        gppString: encoded.gppString,
        usPrivacy: US_PRIVACY_NOT_APPLICABLE,
        updatedAt: new Date().toISOString(),
      };
      this.persist(stored);
      this.reopened = false;
      this.publish(true);
      this.bannerOpen$.next(false);
    } catch {
      this.saveError$.next('Could not save privacy choices. Try again.');
    } finally {
      this.saving$.next(false);
    }
  }

  clear(): void {
    this.reopened = false;
    this.stored = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable — still proceed with cookie/state cleanup below so
      // AuthService.logout() can continue to the Auth0 logout call.
    }
    this.deleteCookie(TCF_COOKIE);
    this.deleteCookie(GPP_COOKIE);
    this.deleteCookie(US_PRIVACY_COOKIE);
    this.publish(this.snapshot.regionResolved);
    this.bannerOpen$.next(false);
  }

  adRequestParams(): AdRequestConsentParams {
    if (this.snapshot.regionResolved && !this.snapshot.inScope) {
      return {};
    }
    // Region not resolved yet (async geo lookup still pending): signal that GDPR
    // may apply without a consent string, rather than omitting gdpr entirely.
    const params: AdRequestConsentParams = { gdpr: '1' };
    if (!this.stored?.tcString || !this.stored.gppString) {
      return params;
    }
    params.gdpr_consent = this.stored.tcString;
    params.us_privacy = this.stored.usPrivacy || US_PRIVACY_NOT_APPLICABLE;
    params.gpp = this.stored.gppString;
    params.gpp_sid = TCF_EU_GPP_SID;
    return params;
  }

  appendToUrl(url: string): string {
    return appendConsentQuery(url, this.adRequestParams());
  }

  private applyRegion(inScope: boolean, country: string | null): void {
    this.snapshot = {
      ...this.snapshot,
      regionResolved: true,
      inScope,
      region: country,
      purposes: TCF_PURPOSE_GROUPS,
      choices: this.stored?.choices ?? null,
      recorded: !!this.stored?.tcString,
    };
    this.snapshot$.next(this.snapshot);
    this.bannerOpen$.next(inScope && (!this.snapshot.recorded || this.reopened));
  }

  private publish(regionResolved: boolean): void {
    this.snapshot = {
      regionResolved,
      inScope: this.snapshot.inScope,
      region: this.snapshot.region,
      purposes: TCF_PURPOSE_GROUPS,
      choices: this.stored?.choices ?? null,
      recorded: !!this.stored?.tcString,
    };
    this.snapshot$.next(this.snapshot);
  }

  private persist(stored: StoredConsent): void {
    this.stored = stored;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Continue with cookie persistence when browser storage is unavailable.
    }
    this.writeCookie(TCF_COOKIE, stored.tcString);
    this.writeCookie(GPP_COOKIE, stored.gppString);
    this.writeCookie(US_PRIVACY_COOKIE, stored.usPrivacy);
  }

  private readStored(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as StoredConsent;
      if (parsed?.version !== 1 || !parsed.tcString || !parsed.gppString || !parsed.choices) {
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  private writeCookie(name: string, value: string): void {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
  }
}
